import { create } from 'zustand';
import type { CrossTenantProduct } from '@/api/products';
import { cartApi } from '@/api/cartApi';

export interface CartItem {
  key: string;
  product: CrossTenantProduct;
  qty: number;
}

interface CartState {
  items: CartItem[];
  /** Set when a REPRESENTATIVE is placing an order on behalf of a retailer. */
  selectedRetailerId?: number;
  selectedRetailerName?: string;

  /** Load cart from backend (call once after login). */
  loadFromBackend: () => Promise<void>;
  /** Persist current items to backend (debounced internally). */
  syncToBackend: () => void;

  addItems: (newItems: CartItem[]) => void;
  removeItem: (key: string) => void;
  updateQty: (key: string, qty: number) => void;
  setRetailer: (id?: number, name?: string) => void;
  clearSupplier: (distId: number) => void;
  clear: () => void;
}

// Debounce timer for backend sync (avoids a write on every keystroke)
let syncTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSyncToBackend(getItems: () => CartItem[]) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    cartApi.saveCart(getItems()).catch(() => {/* silent — local state is source of truth */});
  }, 800);
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  selectedRetailerId: undefined,
  selectedRetailerName: undefined,

  loadFromBackend: async () => {
    try {
      const { items } = await cartApi.getCart();
      if (Array.isArray(items)) {
        set({ items });
      }
    } catch { /* silent */ }
  },

  syncToBackend: () => scheduleSyncToBackend(() => get().items),

  addItems: (newItems) => {
    set((state) => {
      const updated = [...state.items];
      for (const item of newItems) {
        const idx = updated.findIndex(
          (i) =>
            i.product.productId === item.product.productId &&
            i.product.distributorTenantId === item.product.distributorTenantId,
        );
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], qty: updated[idx].qty + item.qty };
        } else {
          updated.push({ ...item });
        }
      }
      return { items: updated };
    });
    get().syncToBackend();
  },

  removeItem: (key) => {
    set((state) => ({ items: state.items.filter((i) => i.key !== key) }));
    get().syncToBackend();
  },

  updateQty: (key, qty) => {
    set((state) => ({
      items: state.items.map((i) => (i.key === key ? { ...i, qty } : i)),
    }));
    get().syncToBackend();
  },

  setRetailer: (id, name) =>
    set({ selectedRetailerId: id, selectedRetailerName: name }),

  clearSupplier: (distId) => {
    set((state) => ({
      items: state.items.filter((i) => i.product.distributorTenantId !== distId),
    }));
    get().syncToBackend();
  },

  clear: () => {
    set({ items: [], selectedRetailerId: undefined, selectedRetailerName: undefined });
    cartApi.clearCart().catch(() => {});
  },
}));
